import { toast } from 'sonner';
import { createMutationHook } from '@/hooks/lib/createMutationHook';
import { apiPost, operationsClient } from '@/lib/api/client';
import { taxCodeEndpoints } from '@/lib/api/endpoints';
import { taxGroupKeys, taxRateKeys, taxZoneKeys } from '@/lib/queryKeys';
import type { TaxRate, CreateTaxRateDto } from '@/types/taxRate';
import { SETTINGS_TAX_RATE_LIST_PARAMS } from './useTaxRates';

function toCreateTaxCodeDto({ isDefault: _isDefault, ...dto }: CreateTaxRateDto) {
  return dto;
}

const SETTINGS_TAX_RATE_LIST_LIMIT = SETTINGS_TAX_RATE_LIST_PARAMS.limit;

function upsertTaxRate(current: TaxRate[] | undefined, next: TaxRate): TaxRate[] {
  const rows = current?.some((taxRate) => taxRate.id === next.id)
    ? current.map((taxRate) => (taxRate.id === next.id ? next : taxRate))
    : [next, ...(current ?? [])];

  return rows
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, SETTINGS_TAX_RATE_LIST_LIMIT);
}

export const useCreateTaxRate = createMutationHook<TaxRate, CreateTaxRateDto>({
  mutationFn: (dto) =>
    apiPost<TaxRate, ReturnType<typeof toCreateTaxCodeDto>>(
      operationsClient,
      taxCodeEndpoints.create(),
      toCreateTaxCodeDto(dto),
    ),
  onSuccess: (data, _variables, queryClient) => {
    queryClient.setQueryData<TaxRate[]>(
      taxRateKeys.list(SETTINGS_TAX_RATE_LIST_PARAMS),
      (current) => upsertTaxRate(current, data),
    );
    void queryClient.invalidateQueries({ queryKey: taxRateKeys.all() });
    void queryClient.invalidateQueries({ queryKey: taxZoneKeys.all() });
    void queryClient.invalidateQueries({ queryKey: taxGroupKeys.all() });
    toast.success('Tax rate created');
  },
});
